import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BOOKING_URL, CONTACT_EMAIL } from '@/config/constants';

interface FooterProps {
  user: any;
}

const Footer: React.FC<FooterProps> = ({ user }) => {
  return (
    <footer className="bg-techne-purple text-white">
      {/* CTA Strip */}
      <div className="border-b border-white/20">
        <div className="container-custom py-12">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">
              Ready to strengthen AI oversight?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-white text-techne-purple hover:bg-gray-100"
              >
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Book a Chair Call
                </a>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="outline"
                className="border-white/50 text-white hover:bg-white/10"
              >
                <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  <span className="hidden sm:inline">Email {CONTACT_EMAIL}</span>
                  <span className="sm:hidden">Email Us</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="container-custom py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
          {/* Services Column */}
          <div>
            <h3 className="font-semibold text-base mb-4">Services</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/board-briefings" className="text-gray-300 hover:text-white transition-colors inline-block py-1">
                  Board Briefings
                </Link>
              </li>
              <li>
                <Link to="/scenario-activations" className="text-gray-300 hover:text-white transition-colors inline-block py-1">
                  Scenario Activations
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-gray-300 hover:text-white transition-colors inline-block py-1">
                  Courses
                </Link>
              </li>
            </ul>
          </div>

          {/* Start Here Column */}
          <div>
            <h3 className="font-semibold text-base mb-4">Start Here</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/quiz" className="text-gray-300 hover:text-white transition-colors inline-block py-1">
                  Assessment
                </Link>
              </li>
              <li>
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-gray-300 hover:text-white transition-colors inline-flex items-center gap-1 py-1">
                  <Mail size={12} />
                  <span className="hidden sm:inline">Email {CONTACT_EMAIL}</span>
                  <span className="sm:hidden">Email Us</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-semibold text-base mb-4">Company</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-gray-300 hover:text-white transition-colors inline-block py-1">
                  About
                </Link>
              </li>
              <li>
                <a 
                  href="https://techne.ai/privacy-policy" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors flex items-center gap-1 py-1"
                >
                  Terms <ExternalLink size={12} />
                </a>
              </li>
              <li>
                <a 
                  href="https://techne.ai/privacy-policy" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors flex items-center gap-1 py-1"
                >
                  Privacy <ExternalLink size={12} />
                </a>
              </li>
              <li>
                <Link to="/login" className="text-gray-300 hover:text-white transition-colors inline-block py-1">
                  Log in
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-10 pt-6 border-t border-white/20">
          <p className="text-xs text-gray-400 max-w-3xl">
            BoardSight provides governance education and advisory support designed for board oversight. 
            BoardSight does not provide legal advice. For jurisdiction-specific legal interpretation, 
            consult qualified counsel.
          </p>
        </div>

        {/* Copyright */}
        <div className="mt-4 text-center text-sm text-gray-400">
          <p>© {new Date().getFullYear()} BoardSight. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
